/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import {TableBody, TableCell, TableRow} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import {makeStyles} from '@material-ui/core/styles';
import Table from '@material-ui/core/Table/Table';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import get from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getDevices} from '@ion/core/src/services/Connect';
import {
  Devices,
  EntityStatus,
  SentinelDevice,
  SentinelDevices
} from '@ion/core/src/types/entities';
import {HierLevel} from '@ion/core/src/types/services';
import {IconButton as Button} from '@ion/dls/src/components/buttons/IconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import DeletePrompt from '@ion/dls/src/editors/DeletePrompt';
import DeviceChooser from '@ion/dls/src/editors/DeviceChooser';
import {DeleteIcon} from '@ion/dls/src/icons/DeleteIcon';
import {HelpCenterIcon} from '@ion/dls/src/icons/HelpCenterIcon';
import {PairIcon} from '@ion/dls/src/icons/PairIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {devicesIncludeSubAccountsSelector} from '../pages/devices/state-selectors';

export interface SentinelDevicesTableProps {
  devices: SentinelDevices;
  devicesRef?: any;
  serviceStatus: EntityStatus;
  identifier?: string;
  accountId?: number;
};

const useStyles = makeStyles(() => ({
  value : {
    fontFamily : 'Roboto',
    fontSize   : '12px',
    lineHeight : '16px',
    color      : dlsColor('grey2')
  },
  paginationContainer : {
    backgroundColor : dlsColor('white'),
    outline         : `1px solid ${dlsColor('grey5')}`,
    width           : '100%',
    display         : 'flex',
    justifyContent  : 'end',
    alignItems      : 'center',
    marginTop       : '10px'
  },
  deviceTableContainer : {
    paddingTop    : 15,
    paddingBottom : 30
  },
  deviceTable : {
    backgroundColor : dlsColor('white')
  },
  headers : {
    display        : 'flex',
    justifyContent : 'space-between',
    fontSize       : '14px',
    fontWeight     : 500,
    paddingBottom  : 10,
    color          : dlsColor('grey2'),
    textTransform  : 'uppercase',
    alignItems     : 'center'
  },
  requiredAsterisk : {
    color  : dlsColor('aaBlue'),
    margin : '0 2px'
  },
  primaryCell : {
    width        : '45%',
    fontSize     : 16,
    padding      : '0px 0px 0px 10px',
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  deleteCell : {
    width        : '5%',
    padding      : 0,
    borderBottom : `1px solid ${dlsColor('grey5')}`,
    borderLeft   : `1px solid ${dlsColor('grey5')}`
  },
  pairCell : {
    textAlign       : 'center',
    padding         : 0,
    width           : '5%',
    borderBottom    : `1px solid ${dlsColor('grey5')}`,
    backgroundColor : dlsColor('grey8')
  },
  secondaryCell : {
    width        : '45%',
    padding      : 0,
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  tableHeader : {
    display    : 'flex',
    alignItems : 'center'
  },
  syncStatus : {
    color : dlsColor('aaBlue')
  },
  secondaryheader : {
    paddingLeft : 20
  },
  noData : {
    ...dlsTypography('body1'),
    alignItems     : 'center',
    textAlign      : 'center',
    fontSize       : 16,
    height         : 50,
    display        : 'flex',
    justifyContent : 'center'
  }
}));

const ITEMS_PER_PAGE = 20;

/**
 * The SentinelDevicesTable component provides a Table component with two columns Primary, Secondary devices
 * and Delete action.
 *
 * ```typescript
 *   import SentinelDevicesTable from '@ion/applications/src/bulk/SentinelDevicesTable';
 *
 *   <SentinelDevicesTable devices={devices} ... />
 * ```
 */
export const SentinelDevicesTable = ({
  devices,
  devicesRef,
  accountId,
  identifier,
  serviceStatus
}: SentinelDevicesTableProps) => {
  const classes                                   = useStyles();
  const includeSubAccounts                        = useSelector(devicesIncludeSubAccountsSelector);
  const [fetch, setFetch]                         = useState(false);
  const [showPrompt, setShowPrompt]               = useState(false);
  const [selectedDevice, setSelectedDevice]       = useState<SentinelDevice>({primaryDevice: '', secondaryDevice: ''});
  const [pairedDevices, setPairedDevices]         = useState(devices);
  const [secondaryDevices, setSecondaryDevices]   = useState<Devices>([]);
  const [selectedSecondary, setSelectedSecondary] = useState<string[]>([]);
  const [currentPage, setCurrentPage]             = useState(1);
  const totalPages                                = Math.ceil(devices.length / ITEMS_PER_PAGE);
  const startItem                                 = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem                                   = Math.min(currentPage * ITEMS_PER_PAGE, devices.length);

  useEffect(() => {
    setPairedDevices(devices);
    setCurrentPage(1);
  }, [devices]);

  useEffect(() => {
    const fetchSecondaryDevices = async() => {
      try {
        const results =  await getDevices(accountId, includeSubAccounts ? HierLevel.ALL : '', {type: get('atuTypeId')});
        setFetch(true);
        setSecondaryDevices(results);
      } catch (err) {
        error(`Device: Error occurred retrieving Devices for account ${accountId}`);
      }
    };
    fetchSecondaryDevices();
  }, []);

  useEffect(() => {
    setSelectedSecondary(pairedDevices?.map(({secondaryDevice}) => secondaryDevice)?.filter(Boolean));
  }, [pairedDevices, identifier]);

  const handleDelete = function({primaryDevice}) {
    const updatedDevices = pairedDevices.map(pairedDevice =>
      pairedDevice.primaryDevice === primaryDevice
        ? {...pairedDevice, secondaryDevice: ''}
        : pairedDevice
    );
    
    const updatedDevs = devicesRef?.current?.map(pairedDevice =>
      pairedDevice.primaryDevice === primaryDevice
        ? {...pairedDevice, secondaryDevice: ''}
        : pairedDevice
    );
    devicesRef.current = updatedDevs;
    setPairedDevices(updatedDevices);
  };

  const handleDeviceChange = function(primaryDevice) {
    return (newSecondaryDevice) => {
      const updatedDevices = pairedDevices.map(pairedDevice =>
        pairedDevice.primaryDevice === primaryDevice
          ? {...pairedDevice, secondaryDevice: newSecondaryDevice?.[identifier]}
          : pairedDevice
      );
      
      const updatedDevs = devicesRef?.current?.map(pairedDevice =>
        pairedDevice.primaryDevice === primaryDevice
          ? {...pairedDevice, secondaryDevice: newSecondaryDevice?.[identifier]}
          : pairedDevice
      );
      devicesRef.current = updatedDevs;
      setPairedDevices(updatedDevices);
    };
  };
  
  const currentDevices = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return pairedDevices?.slice(startIndex, endIndex);
  }, [currentPage, pairedDevices]);
  
  const handlePreviousPage = useCallback(function() {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);
  
  const handleNextPage = useCallback(function() {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const getSyncedDevicesCount = useMemo(() => {
    const syncedDevices = devicesRef?.current?.filter(device => device?.secondaryDevice !== '');
    return syncedDevices?.length;
  }, [devicesRef?.current]);

  const onDeleteCancel = function() {
    setShowPrompt(false);
    setSelectedDevice({primaryDevice: '', secondaryDevice: ''});
  };

  const onDeleteConfirm = useCallback(function() {
    setShowPrompt(false);
    handleDelete(selectedDevice);
  }, [selectedDevice]);

  const onDeleteClick = function(selectedDevice) {
    return () => {
      setShowPrompt(true);
      setSelectedDevice(selectedDevice);
    };
  };

  const overrideDeleteMesssage = useCallback(function() {
    return translate('common:editors.bulkSentinel.deleteMessage', {primary: selectedDevice.primaryDevice, secondary: selectedDevice.secondaryDevice});
  }, [selectedDevice]);

  const renderOption = function(option) {
    if (option.disabled) {
      return (
        <><div>{option.label}</div><div style={{paddingLeft: 10}}><PairIcon /></div></>
      );
    } else {
      return (<div>{option.label}</div>);
    }
  };

  return (<>
    <div className={classes.deviceTableContainer}>
      <div>
        <div className={classes.headers}>
          <div className={classes.tableHeader}>
            <span>{translate('common:editors.bulkSentinel.primaryDevices')}</span>
            <span className={classes.requiredAsterisk}>*</span>
            <Tooltip arrow title={translate('common:editors.bulkSentinel.serviceMsg')}>
              <span><HelpCenterIcon iconSize={20}/></span>
            </Tooltip>
          </div>
          <div className={classes.secondaryheader}>
            <span>{translate('common:editors.bulkSentinel.secondaryDevices')}</span>
            <span className={classes.requiredAsterisk}>*</span>
          </div>
          <div className={classes.syncStatus}>
            {translate('common:editors.bulkSentinel.synced', {pairedDevices: getSyncedDevicesCount, total: devices?.length})}
          </div>
        </div>
      </div>
      <Table style={{paddingBottom: 10}}>
        <TableBody className={classes.deviceTable}>
          {currentDevices?.length ? (currentDevices?.map(({primaryDevice, secondaryDevice}) => {
            return (
              <TableRow key={`row-${primaryDevice}`}>
                <TableCell className={classes.primaryCell}>{primaryDevice}</TableCell>
                <TableCell className={classes.pairCell}><PairIcon /></TableCell>
                <TableCell className={classes.secondaryCell}>
                  <DeviceChooser isSecondary
                    customPlaceholder={translate('common:editors.bulkSentinel.secondaryPlaceholder')}
                    renderOption={renderOption}
                    readOnly={serviceStatus === EntityStatus.Disabled}
                    loading={!fetch}
                    accountId={accountId}
                    identifier={identifier}
                    selectedSecondary={selectedSecondary}
                    secondaryDevices={secondaryDevices}
                    defaultValue={secondaryDevice}
                    onChange={handleDeviceChange(primaryDevice)}/></TableCell>
                <TableCell className={classes.deleteCell}>
                  <Button disabled={serviceStatus === EntityStatus.Disabled}
                    icon={<DeleteIcon size={IconSize.medium} />}
                    onClick={onDeleteClick({primaryDevice, secondaryDevice})} />
                </TableCell>
              </TableRow>);
          })) : <TableRow>
            <div className={classes.noData}>{translate('common:editors.bulkSentinel.noDataMsg')}</div>
          </TableRow>}
        </TableBody>
      </Table>

      <Render.If condition={devices?.length > ITEMS_PER_PAGE}>
        <div className={classes.paginationContainer}>
          <span className={classes.value}>
            {translate('jobcreate:conflict.table.pagination.rowsPerPage', {from: startItem, to: endItem, count: devices.length})}
          </span>
          <div>
            <IconButton onClick={handlePreviousPage} disabled={currentPage === 1}>
              <ChevronLeft />
            </IconButton>
            <span className={classes.value}>
              {currentPage} / {totalPages}
            </span>
            <IconButton onClick={handleNextPage} disabled={currentPage === totalPages}>
              <ChevronRight />
            </IconButton>
          </div>
        </div>
      </Render.If>
    </div>
    <DeletePrompt singleDeleteMessage
      items={[selectedDevice]}
      show={showPrompt}
      overrideMessage={overrideDeleteMesssage}
      entity={translate('common:editors.bulkSentinel.secondaryDevice')}
      onConfirm={onDeleteConfirm}
      onCancel={onDeleteCancel} />
  </>
  );
};

export default SentinelDevicesTable;
